import React, { useEffect, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Container,
  Flex,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Tabs,
  Text,
  Title,
  px,
  useMantineTheme,
} from '@mantine/core';
import { DateValue } from '@mantine/dates';
import { useDebouncedValue, useDisclosure, useMediaQuery } from '@mantine/hooks';
import { IconArrowLeft, IconExclamationCircle, IconLogin } from '@tabler/icons-react';
import KiloDateInput from 'components/KiloDateInput/KiloDateInput';
import KiloInput from 'components/KiloInput';
import KiloTabs from 'components/KiloTabs/KiloTabs';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import useKioskEvents from 'hooks/event/useKioskEvents';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { KioskEventList } from 'types/generated';
import { getStartsIn } from 'utils/formatters';

import EmptyEventPlaceholder from './components/EmptyEventPlaceholder';
import EnterKioskModeModal from './components/EnterKioskModeModal';
import EventCard from './components/EventCard';

dayjs.extend(isBetween);

const Kiosk = (): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.sm}`, true);
  const kind = searchParams.get('eventType') as 'class' | 'appointment';

  const [isEnterKioskModalOpened, { open: openKioskModal, close: closeKioskModal }] = useDisclosure(false);

  const [events, setEvents] = useState<KioskEventList[]>([]);

  const [activeTab, setActiveTab] = useState<string>(kind || 'class');
  const [selectedDate, setSelectedDate] = useState<DateValue>(new Date());

  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebouncedValue(search, 250);

  const isKioskMode = localStorage.getItem('isKiosk') === 'true';

  const {
    data: athleteEventsData,
    isLoading: isAthleteEventsLoading,
    isSuccess: isAthleteEventsSuccess,
    isError: isAthleteEventsError,
    isFetched: isAthleteEventsFetched,
  } = useKioskEvents({
    search: debouncedSearch,
    date: dayjs(selectedDate).format('YYYY-MM-DD'),
  });

  useEffect(() => {
    if (isAthleteEventsSuccess && athleteEventsData) {
      const filterEventsByType = (type: typeof activeTab): KioskEventList[] => {
        return athleteEventsData.data.filter(event => {
          switch (type) {
            case 'class':
              return event.eventType === 'kilo_class_type';
            case 'appointment':
              return event.eventType === 'appointment_type';

            default:
              break;
          }
        });
      };

      let filteredEventsByType = filterEventsByType(activeTab);

      if (isKioskMode) {
        const filteredForAthleteView = filteredEventsByType.filter(event => getStartsIn(event) !== 'Ended');
        filteredEventsByType = filteredForAthleteView;
      }

      setEvents(filteredEventsByType);
    } else {
      setEvents([]);
    }
  }, [athleteEventsData, isAthleteEventsSuccess, activeTab, isKioskMode]);

  const chooseEvent = (eventId: string): void => {
    if (!athleteEventsData?.data) return;
    const selectedEvent = athleteEventsData.data.filter(event => event.eventId === eventId)[0];

    const eventType = selectedEvent.eventType === 'kilo_class_type' ? 'class' : 'appointment';

    navigate(`/kiosk/${selectedEvent.eventId}?eventType=${eventType}`);
  };

  const handleMobileRedirect = (): void => {
    navigate('/');
  };

  const KioskContent = (): JSX.Element => {
    if (isAthleteEventsFetched && !events.length) {
      return <EmptyEventPlaceholder activeTab={activeTab} />;
    }
    return (
      <>
        <KiloInput
          variantStyle='kiosk'
          label={`Select ${activeTab === 'class' ? 'a class' : 'an appointment'} to check in`}
          placeholder={`Search for ${activeTab} or coach`}
          value={search}
          onChange={input => setSearch(input.currentTarget.value)}
        />

        <Stack>
          {isAthleteEventsError && (
            <Center sx={{ flexGrow: 1 }} p='lg'>
              <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
                Something went wrong while fetching data!
              </Alert>
            </Center>
          )}

          {isAthleteEventsLoading && (
            <Center p='xl'>
              <Loader />
            </Center>
          )}

          {events.map(event => (
            <EventCard
              onClick={chooseEvent}
              key={event.eventId}
              event={event}
              eventType={activeTab}
              isDesktop={isDesktop}
            />
          ))}
        </Stack>
      </>
    );
  };

  return (
    <>
      <ScrollArea bg='white' w={'100%'}>
        <Container py='xl' w='100%'>
          <Stack spacing='xl'>
            <Flex direction={isDesktop ? 'row' : 'column'} align='top' gap={isDesktop ? '0px' : 'xl'}>
              <Stack spacing={0} w={'100%'}>
                {isDesktop ? (
                  <>
                    <Text weight={600} size={px(theme.fontSizes.xl) + 1.5} color='dimmed'>
                      {!isKioskMode ? 'Staff' : 'Athlete'} Kiosk
                    </Text>

                    <Title order={1} size={px(theme.spacing.xl) * 2}>
                      Upcoming Events
                    </Title>
                    {!isKioskMode && (
                      <Title order={1} weight={400} size={theme.fontSizes.sm} color='dimmed'>
                        Don&apos;t forget to enter athlete mode to avoid athletes from accessing delicate
                        information.
                      </Title>
                    )}
                  </>
                ) : (
                  <Group noWrap spacing={'sm'}>
                    {!isKioskMode && (
                      <ActionIcon onClick={handleMobileRedirect} variant='light' color='kiloBlue'>
                        <IconArrowLeft />
                      </ActionIcon>
                    )}
                    <Title order={1} size={px(theme.spacing.sm) * 2}>
                      Upcoming Events
                    </Title>
                  </Group>
                )}
              </Stack>

              {!isKioskMode && (
                <Group position='apart'>
                  {!isDesktop && (
                    <KiloDateInput
                      onChange={value => setSelectedDate(value)}
                      value={selectedDate}
                      variantStyle='kiosk'
                      w={isDesktop ? 'fit-content' : '50%'}
                    />
                  )}
                  <Button
                    leftIcon={isDesktop ? <IconLogin size={theme.fontSizes.xl} /> : null}
                    onClick={openKioskModal}
                    data-cy='enterKioskModeBtn'
                    size={isDesktop ? 'lg' : 'sm'}
                  >
                    Enter Athlete mode
                  </Button>
                </Group>
              )}
            </Flex>
            {!isKioskMode && isDesktop && (
              <KiloDateInput
                onChange={value => setSelectedDate(value)}
                value={selectedDate}
                variantStyle='kiosk'
                w={isDesktop ? 'fit-content' : '50%'}
              />
            )}

            <KiloTabs activeTab={activeTab} setActiveTab={setActiveTab} mt={32}>
              <>
                <Tabs.Tab value='class'>Classes</Tabs.Tab>
                <Tabs.Tab value='appointment'>Appointments</Tabs.Tab>
              </>
            </KiloTabs>
            <KioskContent />
          </Stack>
        </Container>
      </ScrollArea>

      <EnterKioskModeModal opened={isEnterKioskModalOpened} onClose={closeKioskModal} />
    </>
  );
};

export default Kiosk;
